import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'

import {
  ChevronDownIcon,
  EmptyState,
  FlagIcon,
  Flex,
  Heading,
  Skeleton,
} from '@buffer-mono/popcorn'

import { TakeawaysGetStarted } from '../TakeawaysGetStarted'
import { TakeawayCard } from './TakeawayCard'
import { TakeawaysProvider } from './TakeawaysContext'
import { getColorIndices } from './iconColors'
import type { TakeawaysProps } from './types'

import styles from './Takeaways.module.css'

export const MAX_VISIBLE_TAKEAWAYS = 3

const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2']

function SkeletonCard(): JSX.Element {
  return (
    <Flex direction="column" gap="xs" className={styles.takeawayCard}>
      <Skeleton className={styles.skeletonBadge} />
      <Skeleton className={styles.skeletonTitle} />
      <Flex direction="column" gap="xs" className={styles.skeletonCardBody}>
        <Skeleton className={styles.skeletonLine} />
        <Skeleton className={styles.skeletonLine} />
      </Flex>
    </Flex>
  )
}

export function Takeaways({
  takeaways,
  loading,
  refreshing = false,
  variant = 'single-channel',
  channels,
  badge,
  channelService,
  onOpenRecommendedTimesDialog,
  canCreatePosts = true,
  gate,
  channelId,
}: TakeawaysProps): JSX.Element {
  // A card with no channel to act on cannot render, so drop those here and let
  // the empty state below count only what will actually appear.
  const visible = useMemo(
    () =>
      takeaways
        .filter((takeaway) => takeaway.channelIds?.[0])
        .slice(0, MAX_VISIBLE_TAKEAWAYS),
    [takeaways],
  )

  // These colours only show on a single-channel page, where every card shares
  // the page's channel — so the first card's channel keys the whole section.
  const sectionChannelId = visible[0]?.channelIds?.[0]
  const colorIndices = useMemo(
    () =>
      sectionChannelId ? getColorIndices(sectionChannelId, visible.length) : [],
    [sectionChannelId, visible.length],
  )

  const [open, setOpen] = useState(true)

  // When there are no takeaways the section is a standalone empty state: drop
  // the "Weekly Takeaways" disclosure header (there's nothing to collapse) so
  // the empty state reads as the whole card.
  const isEmpty = !loading && !gate.loading && visible.length === 0

  // Too few posts to analyse: the section becomes the get-started panel, which
  // owns its own surface and needs none of the disclosure chrome below.
  if (!gate.loading && !gate.shouldRequestTakeaways) {
    return (
      <TakeawaysGetStarted
        channelId={channelId}
        canCreatePosts={canCreatePosts}
      />
    )
  }

  return (
    <TakeawaysProvider
      variant={variant}
      channels={channels}
      channelService={channelService}
      canCreatePosts={canCreatePosts}
      onOpenRecommendedTimesDialog={onOpenRecommendedTimesDialog}
    >
      <Flex
        as="section"
        direction="column"
        gap="2xs"
        className={styles.container}
      >
        {!isEmpty && (
          <Heading as="h2" size="xs" className={styles.heading}>
            <button
              type="button"
              className={styles.header}
              onClick={(): void => setOpen((isOpen) => !isOpen)}
              aria-expanded={open}
            >
              <span>Weekly Takeaways</span>
              {badge != null && <span className={styles.badge}>{badge}</span>}
              <ChevronDownIcon
                aria-hidden
                className={clsx(styles.chevron, open && styles.chevronOpen)}
              />
            </button>
          </Heading>
        )}
        {open && (loading || gate.loading) && (
          <div className={styles.grid}>
            {SKELETON_KEYS.map((key) => (
              <SkeletonCard key={key} />
            ))}
          </div>
        )}
        {isEmpty && (
          <EmptyState size="small" className={styles.emptyState}>
            <EmptyState.Icon>
              <FlagIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>No takeaways yet</EmptyState.Heading>
            <EmptyState.Description>
              We don&apos;t have enough data to surface insights for this period
              yet. Keep posting and check back soon.
            </EmptyState.Description>
          </EmptyState>
        )}
        {!loading && !gate.loading && visible.length > 0 && (
          <div
            className={clsx(styles.grid, refreshing && styles.refreshing)}
            aria-busy={refreshing}
          >
            {visible.map((takeaway, index) => (
              <TakeawayCard
                key={
                  takeaway.id ??
                  `${takeaway.category}-${takeaway.metadata?.createPostType ?? 'default'}-${index}`
                }
                takeaway={takeaway}
                colorIndex={colorIndices[index]}
                cardPosition={index}
                collapsed={!open}
              />
            ))}
          </div>
        )}
      </Flex>
    </TakeawaysProvider>
  )
}
